import { Flex, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import Button from 'common/components/button';
import { coverageTypes, planPaymentMode } from 'common/utils/enums';
import { formatDateToHumanDate } from 'common/utils/formDateToHumanTime';
import FormatAsCurrency from 'common/utils/formatAsCurrency';
import { selectUpfrontSplitAmount } from 'redux/features/plans/plansSlice';
import { useEffect, useState } from 'react';
import { isEmpty } from 'common/utils/isEmpty';
import moment from 'moment';
import { RefundPolicyLink } from '../refund-policy-modal';

const PaymentLine = ({ line, plan, getMonthlyStartDateText }) => (
  <div className='grid grid-flow-dense grid-cols-[1fr_90px] gap-1 text-base'>
    <span className='font-medium'>{line.title}</span>
    <span className='text-sm text-gray-500'>
      {line.isMonthly
        ? 'Starting '
        : line.selected
          ? 'Payment due '
          : line.paid
            ? 'Payment completed '
            : 'Payment not completed '}
      {line.isMonthly
        ? getMonthlyStartDateText({ startDate: line.date, isPaid: line.paid })
        : line?.date
          ? formatDateToHumanDate(line.date)
          : line?.paid
            ? 'at signup'
            : 'today'}
    </span>
    <span className='font-medium row-start-1 col-start-2 row-span-2'>
      {FormatAsCurrency(line.amount, plan?.lease?.currency)}
      {line.isMonthly && ' /mo'}
    </span>
  </div>
);

const PaymentSummaryCard = ({
  plan,
  coverageSelection,
  onClick,
  isLoading = true,
  isComplete = false,
}) => {
  // Check eligibility of coverages
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Get data from plan and coverageSelection
  const leaveAnytimeSelection =
    coverageSelection?.[coverageTypes.LEAVE_ANYTIME];
  const cancelAnytimeSelection =
    coverageSelection?.[coverageTypes.CANCEL_ANYTIME];

  const leaveAnytimePlan = plan?.coverage?.leaveAnytime;
  const cancelAnytimePlan = plan?.coverage?.cancelAnytime;

  // Get upfront split from redux
  const upfrontSplit = useSelector(selectUpfrontSplitAmount);
  const leaveAnytimeSplitTotal = leaveAnytimePlan?.amount || 0;
  const monthlySplitTotal = leaveAnytimeSplitTotal - upfrontSplit;
  const monthlySplitAmount = monthlySplitTotal / (leaveAnytimePlan?.term || 1);

  const cancelAnytimeParts = [
    {
      key: 'cancelAnytime',
      lineKey: 'cancelAnytime',
      title: 'Upfront Payment',
      date: cancelAnytimePlan?.payment?.stripe?.paymentDate,
      amount: cancelAnytimePlan?.payment?.amount || 0,
      selected: cancelAnytimeSelection === planPaymentMode.ONE_TIME,
      paid: !!cancelAnytimePlan?.payment?.stripe?.id,
      isMonthly: false,
    },
  ];

  const leaveAnytimeParts = [
    {
      key: 'leaveAnytimeOnetime',
      lineKey: 'leaveAnytimeUpfront',
      title: 'Upfront Payment',
      date: leaveAnytimePlan?.payment?.onetime?.stripe?.paymentDate,
      amount: leaveAnytimePlan?.payment?.onetime?.amount || 0,
      selected: leaveAnytimeSelection === planPaymentMode.ONE_TIME,
      paid: !!leaveAnytimePlan?.payment?.onetime?.stripe?.id,
      isMonthly: false,
    },
    {
      key: 'leaveAnytimeSplitOnetime',
      lineKey: 'leaveAnytimeUpfront',
      title: 'Upfront Payment',
      date: leaveAnytimePlan?.payment?.split?.onetime?.stripe?.paymentDate,
      amount:
        leaveAnytimePlan?.payment?.split?.onetime?.amount || upfrontSplit || 0,
      selected: leaveAnytimeSelection === planPaymentMode.SPLIT,
      paid: !!leaveAnytimePlan?.payment?.split?.onetime?.stripe?.id,
      isMonthly: false,
    },
    {
      key: 'leaveAnytimeSubscription',
      lineKey: 'leaveAnytimeMonthly',
      title: `Monthly Installments (x${leaveAnytimePlan?.term})`,
      date:
        leaveAnytimePlan?.payment?.subscription?.stripe?.paymentDate ||
        leaveAnytimePlan?.startDate,
      amount: leaveAnytimePlan?.payment?.subscription?.amount || 0,
      selected: leaveAnytimeSelection === planPaymentMode.SUBSCRIPTION,
      paid: !!leaveAnytimePlan?.payment?.subscription?.stripe?.id,
      isMonthly: true,
    },
    {
      key: 'leaveAnytimeSplitSubscription',
      lineKey: 'leaveAnytimeMonthly',
      title: `Monthly Installments (x${leaveAnytimePlan?.term})`,
      date:
        leaveAnytimePlan?.payment?.split?.subscription?.stripe?.paymentDate ||
        leaveAnytimePlan?.startDate,
      amount:
        leaveAnytimePlan?.payment?.split?.subscription?.amount ||
        monthlySplitAmount ||
        0,
      selected: leaveAnytimeSelection === planPaymentMode.SPLIT,
      paid: !!leaveAnytimePlan?.payment?.split?.subscription?.stripe?.id,
      isMonthly: true,
    },
  ];

  const processPaymentParts = (parts) =>
    Object.values(
      parts
        .filter((part) => (part.selected || part.paid) && part.amount > 0)
        .reduce((acc, part) => {
          const { lineKey, title, date, amount, isMonthly, selected, paid } =
            part;
          if (!acc[lineKey]) {
            acc[lineKey] = {
              key: lineKey,
              title,
              date,
              amount: 0,
              isMonthly,
              selected,
              paid,
            };
          }
          acc[lineKey].amount += amount;
          return acc;
        }, {})
    );

  const cancelAnytimeLines = processPaymentParts(cancelAnytimeParts);
  const leaveAnytimeLines = processPaymentParts(leaveAnytimeParts);

  const getMonthlyStartDateText = ({ startDate, isPaid = false }) => {
    if (!startDate) return 'today';
    const startMoment = moment(startDate);
    const today = moment(now);

    if (startMoment.isSameOrBefore(today, 'day')) {
      if (isPaid) {
        return `from ${startDate ? formatDateToHumanDate(startDate) : 'signup'}`;
      }
      return 'today';
    }
    return formatDateToHumanDate(startDate);
  };

  if (isEmpty(cancelAnytimeLines) && isEmpty(leaveAnytimeLines)) {
    return null;
  }

  return (
    <Flex className={`flex-col gap-5 p-6 border rounded-lg border-gray-200`}>
      <div className='flex flex-col gap-1'>
        <h2 className='capitalize text-lg font-medium'>Payment Summary</h2>
      </div>
      <div className='flex flex-col gap-4 text-base'>
        {!isEmpty(cancelAnytimeLines) && (
          <div className='flex flex-col gap-3 mb-2'>
            <Text className='capitalize text-md font-medium pb-2 border-b border-gray-200'>
              Pre-Lease Coverage
            </Text>
            {cancelAnytimeLines.map((line) => (
              <PaymentLine
                key={line.key}
                line={line}
                plan={plan}
                getMonthlyStartDateText={getMonthlyStartDateText}
              />
            ))}
          </div>
        )}
        {!isEmpty(leaveAnytimeLines) && (
          <div className='flex flex-col gap-3'>
            <Text className='capitalize text-md font-medium pb-2 border-b border-gray-200'>
              Mid-Lease Coverage
            </Text>
            {leaveAnytimeLines.map((line) => (
              <PaymentLine
                key={line.key}
                line={line}
                plan={plan}
                getMonthlyStartDateText={getMonthlyStartDateText}
              />
            ))}
          </div>
        )}
        {(cancelAnytimeLines.length > 0 || leaveAnytimeLines.length > 0) && (
          <div className='grid grid-cols-[1fr_1fr_90px] gap-2 text-md text-gray-500 border-t border-gray-200 pt-2 mt-1'>
            <span className='col-start-3'>No tax</span>
          </div>
        )}
      </div>

      {onClick && (
        <Button
          className='w-full mt-3'
          theme='primary'
          title='Complete Payment'
          onClick={onClick}
          loading={isLoading}
          disabled={isLoading}
        />
      )}

      {!isComplete && (
        <div className='flex flex-col gap-3 text-sm text-gray-500 '>
          <div className='mt-3'>
            <span className='font-semibold'>
              By clicking Complete Payment, you give permission to ReLease to
              charge your provided payment method for the amounts above
            </span>
            , and agree to ReLease's{' '}
            <a
              href='https://www.releaserent.com/legal/terms-of-service'
              target='_blank'
              rel='noopener noreferrer'
              className='hover:underline'
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href='https://www.releaserent.com/legal/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'
              className='hover:underline'
            >
              Privacy Policy
            </a>
            . Based on the coverage selected above, you agree to the{' '}
            <a
              href='https://www.releaserent.com/legal/release-pre-lease-terms-conditions'
              target='_blank'
              rel='noopener noreferrer'
              className='hover:underline'
            >
              ReLease Pre-Lease Coverage
            </a>{' '}
            and the{' '}
            <a
              href='https://www.releaserent.com/legal/release-mid-lease-terms-conditions'
              target='_blank'
              rel='noopener noreferrer'
              className='hover:underline'
            >
              ReLease Mid-Lease Coverage
            </a>
            {' '}Terms and Conditions.
          </div>
          <RefundPolicyLink>Learn more about the ReLease Refund Policy</RefundPolicyLink>
        </div>
      )}
    </Flex>
  );
};

export default PaymentSummaryCard;
